import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportBookingDetails = () => {
  const { id: bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState(null);
  const [hotelConfirmationNo, setHotelConfirmationNo] = useState('');
  const [submittingConfirmation, setSubmittingConfirmation] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(false);

  useEffect(() => {
    if (bookingId) {
      loadBookingDetails();
    }
  }, [bookingId]);

  const loadBookingDetails = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading booking details for ID:', bookingId);
      const data = await supportApiService.getBookingDetailsById(bookingId);
      console.log('📊 Booking Details API Response:', data);
      
      if (data.success) {
        setBookingData(data);
        console.log('✅ Booking details loaded successfully');
      } else {
        console.error('❌ Booking Details API returned error:', data.message);
        toast.error(data.message || 'Failed to load booking details');
      }
    } catch (error) {
      console.error('❌ Booking details load error:', error);
      toast.error('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/support/dashboard/booking-details');
  };

  const handleCancelBooking = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      setCancellingBooking(true);
      console.log('🚫 Cancelling booking:', bookingId);
      
      const data = await supportApiService.cancelBooking(bookingId);
      console.log('📊 Cancel Booking API Response:', data);
      
      if (data.success) {
        toast.success('Booking cancelled successfully!');
        // Reload booking details to show updated status
        await loadBookingDetails();
      } else {
        console.error('❌ Cancel Booking API returned error:', data.message);
        toast.error(data.message || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('❌ Cancel booking error:', error);
      toast.error('Failed to cancel booking');
    } finally {
      setCancellingBooking(false);
    }
  };

  const handleSubmitConfirmation = async (e) => {
    e.preventDefault();
    
    if (!hotelConfirmationNo.trim()) {
      toast.error('Please enter hotel confirmation number');
      return;
    }

    try {
      setSubmittingConfirmation(true);
      console.log('📝 Submitting hotel confirmation:', hotelConfirmationNo);
      
      const data = await supportApiService.saveHotelConfirmationNo(bookingId, hotelConfirmationNo);
      console.log('📊 Hotel Confirmation API Response:', data);
      
      if (data.success) {
        toast.success('Hotel confirmation number saved successfully!');
        setHotelConfirmationNo('');
        // Reload booking details to show updated confirmation
        await loadBookingDetails();
      } else {
        console.error('❌ Hotel Confirmation API returned error:', data.message);
        toast.error(data.message || 'Failed to save hotel confirmation number');
      }
    } catch (error) {
      console.error('❌ Hotel confirmation save error:', error);
      toast.error('Failed to save hotel confirmation number');
    } finally {
      setSubmittingConfirmation(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="text-gray-500">-</span>;
    
    const statusLower = status.toLowerCase();
    let badgeClass = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';
    
    switch (statusLower) {
      case 'booked':
        badgeClass += ' bg-green-100 text-green-800';
        break;
      case 'cancelled':
        badgeClass += ' bg-red-100 text-red-800';
        break;
      case 'hold':
        badgeClass += ' bg-yellow-100 text-yellow-800';
        break;
      default:
        badgeClass += ' bg-gray-100 text-gray-800';
    }
    
    return <span className={badgeClass}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!bookingData || !bookingData.bookingDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-4">Unable to load booking details.</p>
          <button
            onClick={handleBackClick}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
          >
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  const booking = bookingData.bookingDetails;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Page Header */}
        <div className="mb-6">
          <button
            onClick={handleBackClick}
            className="text-green-600 hover:text-green-800 mb-2 flex items-center"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Bookings
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Booking Details</h1>
          <p className="text-gray-600">Detailed information for booking {booking.bookingId}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Booking Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Booking Overview */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Booking Overview</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Booking ID</h4>
                    <p className="text-lg font-semibold text-gray-900">{booking.bookingId}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Status</h4>
                    {getStatusBadge(booking.status)}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Agent ID</h4>
                    <p className="text-gray-900">{booking.agentId || '-'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Booking Date</h4>
                    <p className="text-gray-900">{formatDate(booking.bookingDate)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hotel Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Hotel Information</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Hotel Name</h4>
                    <p className="text-gray-900 font-medium">{booking.hotelName || '-'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">City</h4>
                    <p className="text-gray-900">{booking.city || '-'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Check-in Date</h4>
                    <p className="text-gray-900">{formatDate(booking.checkInDate)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Check-out Date</h4>
                    <p className="text-gray-900">{formatDate(booking.checkOutDate)}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Confirmation Number</h4>
                    <p className="text-gray-900">{booking.confirmationNo || '-'}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-1">Total Amount</h4>
                    <p className="text-gray-900 font-semibold">{formatCurrency(booking.totalAmount)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            {booking.customerName && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Customer Information</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Customer Name</h4>
                      <p className="text-gray-900">{booking.customerName || '-'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Customer Email</h4>
                      <p className="text-gray-900">{booking.customerEmail || '-'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Customer Phone</h4>
                      <p className="text-gray-900">{booking.customerPhone || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Room Details */}
            {booking.rooms && booking.rooms.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Room Details</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    {booking.rooms.map((room, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Room {index + 1}</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Adults:</span>
                            <span className="ml-2 text-gray-900">{room.adults || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Children:</span>
                            <span className="ml-2 text-gray-900">{room.children || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Room Type:</span>
                            <span className="ml-2 text-gray-900">{room.roomType || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Rate:</span>
                            <span className="ml-2 text-gray-900">{formatCurrency(room.rate)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Actions */}
          <div className="space-y-6">
            {/* Summary Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Summary</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Rooms:</span>
                  <span className="font-medium">{bookingData.totalRooms || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Adults:</span>
                  <span className="font-medium">{bookingData.totalAdults || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Total Children:</span>
                  <span className="font-medium">{bookingData.totalChildren || 0}</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between">
                    <span className="text-gray-900 font-medium">Total Amount:</span>
                    <span className="font-bold text-lg">{formatCurrency(booking.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hotel Confirmation Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Hotel Confirmation</h3>
              </div>
              <div className="p-6">
                <form onSubmit={handleSubmitConfirmation}>
                  <div className="mb-4">
                    <label htmlFor="hotelConfirmationNo" className="block text-sm font-medium text-gray-700 mb-2">
                      Hotel Confirmation Number
                    </label>
                    <input
                      type="text"
                      id="hotelConfirmationNo"
                      value={hotelConfirmationNo}
                      onChange={(e) => setHotelConfirmationNo(e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="Enter confirmation number"
                      disabled={submittingConfirmation}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submittingConfirmation || !hotelConfirmationNo.trim()}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium transition-colors"
                  >
                    {submittingConfirmation ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Confirmation'
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Actions</h3>
              </div>
              <div className="p-6 space-y-3">
                <button
                  onClick={handleCancelBooking}
                  disabled={cancellingBooking || booking.status?.toLowerCase() === 'cancelled'}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  {cancellingBooking ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Cancelling...
                    </>
                  ) : (
                    'Cancel Booking'
                  )}
                </button>
                
                <button
                  onClick={() => window.print()}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  <i className="fas fa-print mr-2"></i>
                  Print Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportBookingDetails;